/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';

import { isMobile } from '../../components/breakpoints';

import { Link } from './components/link';

import { contentAlignment } from './ecomm-hero-banner.props.autogenerated';

import { IEcommHeroBannerViewProps } from './ecomm-hero-banner';

/**
 *
 * EcommContentTemplate1View component
 * @extends {React.PureComponent<IEcommHeroBannerViewProps>}
 */
export class EcommHeroBannerView extends React.PureComponent<IEcommHeroBannerViewProps> {
    public state: any = { width: 0, hasMounted: false };

    private telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public componentDidMount(): void {
        if (!this.state.hasMounted) {
            window.addEventListener('resize', this._updateDimensions);

            this.setState({ width: window.innerWidth, hasMounted: true });
        }
    }

    public render(): JSX.Element | null {
        if (!this.state.hasMounted) {
            return null;
        }

        const { config } = this.props;

        const bg = config.bannerImage?.src ? { backgroundImage: `url(${config.bannerImage.src})` } : { backgroundImage: '' };

        let result = 'ecomm-hero-banner';

        result += config.isMainMobileBanner !== undefined && !config.isMainMobileBanner ? ' normal' : '';
        result += config.contentAlignment !== undefined && config.contentAlignment === contentAlignment.right ? ' right' : '';

        return (
            <div className={result} style={bg}>
                {this._renderContent()}
            </div>
        );
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this._updateDimensions);
    }

    private _renderContent(): JSX.Element | null {
        const { config } = this.props;

        if (isMobile(this.state.width)) {
            if (config.isMainMobileBanner !== undefined && !config.isMainMobileBanner) {
                let link = '';

                if (config.mobileLink !== undefined) {
                    // True sets to first button, false sets to second button.
                    if (config.mobileLink) {
                        link =
                            config.firstAnchorTargetID && config.firstAnchorTargetID !== ''
                                ? `#${config.firstAnchorTargetID}`
                                : config.firstLinkUrl
                                ? config.firstLinkUrl.destinationUrl
                                : '';
                    } else {
                        link =
                            config.secondAnchorTargetID && config.secondAnchorTargetID !== ''
                                ? `#${config.secondAnchorTargetID}`
                                : config.secondLinkUrl
                                ? config.secondLinkUrl.destinationUrl
                                : '';
                    }
                }

                return (
                    <div className='ecomm-hero-banner-content'>
                        <a href={link}>
                            <h5 className='caption'>{config.mobileCaption}</h5>
                            <h3 className='title'>{config.mobileTitle}</h3>
                        </a>
                    </div>
                );
            } else {
                return <div className='ecomm-hero-banner-content' />;
            }
        }

        return (
            <div className='ecomm-hero-banner-content'>
                {this._renderTitle()}
                {this._renderDescription() !== null && this._renderDescription()}
                {this._renderButtons()}
            </div>
        );
    }

    private _renderTitle(): JSX.Element | null {
        const { config, title } = this.props;

        let result = <h5>{title}</h5>;

        if (config.enablePrimaryColour) {
            result = <h4 className='enable-primary-colour'>{title}</h4>;
        }

        return <div className='title'>{result}</div>;
    }

    private _renderDescription(): JSX.Element | null {
        const { description } = this.props;

        if (description === null) {
            return null;
        }

        return (
            <div className='description'>
                <p id={this.props.id} {...this.props.renderModuleAttributes(this.props)}>
                    {description}
                </p>
            </div>
        );
    }

    private _renderButtons(): JSX.Element | null {
        const { config } = this.props;

        let first = null;

        if (config.firstAnchorTargetID) {
            first = (
                <a className='first-button' href={`#${config.firstAnchorTargetID}`}>
                    {config.firstButtonText}
                </a>
            );
        } else if (config.firstLinkUrl?.destinationUrl) {
            first = (
                <Link
                    className='first-button'
                    text={config.firstButtonText}
                    context={this.props.context}
                    ariaLabel={config.firstButtonText}
                    href={config.firstLinkUrl && config.firstLinkUrl.destinationUrl}
                    telemetryContent={this.telemetryContent}
                />
            );
        }

        let second = null;

        if (config.secondAnchorTargetID) {
            second = (
                <a className='second-button' href={`#${config.secondAnchorTargetID}`}>
                    {config.secondButtonText}
                </a>
            );
        } else if (config.secondLinkUrl?.destinationUrl) {
            second = (
                <Link
                    className='second-button'
                    text={config.secondButtonText}
                    context={this.props.context}
                    ariaLabel={config.secondButtonText}
                    href={config.secondLinkUrl && config.secondLinkUrl.destinationUrl}
                    telemetryContent={this.telemetryContent}
                />
            );
        }

        return (
            <div className='ecomm-hero-banner-actions'>
                {first}
                {second}
            </div>
        );
    }

    private _updateDimensions = () => {
        this.setState({ width: window.innerWidth });
    };
}

export default EcommHeroBannerView;
